import isPlainObject from 'lodash/isPlainObject'

import { patchGoal, patchIepVisibility, postDocumentExtraction } from '../api'

import {
  CONCERN_STATUS_OPEN,
  CONCERN_STATUS_RESOLVED,
  PLAN_TYPE_504,
  PLAN_TYPE_IEP,
  PLAN_TYPE_IPP,
  RECOMMENDATION_STATUS_DISMISSED,
  REVIEW_STATUS_REVIEWED
} from '../arch/iepConstants'

import { equalArrayWithOrder, idMap } from '../common'
import { trackIepReviewAll, trackIepReviewRecommendations } from '../tracking/segment'

import { UNATTACHED_FILTERS, getSectionHosts, partitionLooseTargets } from './recommendationPlacement'

import {
  ANALYZABLE_DOCUMENTS,
  EXTRACTION_STATUS_COMPLETED,
  EXTRACTION_STATUSES_NOT_RUNNING,
  GOAL_PRIORITIZATION_THRESHOLD,
  IEP_PROGRESS_MILESTONES,
  IEP_TYPE_ACCOMMODATION,
  IEP_TYPE_MODIFICATION,
  IEP_TYPE_PLOP,
  IEP_WIZARD_REVIEW_BASELINE,
  IPP_TYPE_OUTCOME,
  IPP_TYPE_PRIORITY,
  IPP_TYPE_SUPPORT,
  NEW_CONCERN_ID_PLACEHOLDER,
  PLAN_TYPE_GOAL,
  PLAN_TYPE_RECOMMENDATION_CATEGORY,
  PLAN_TYPE_SECTION_SUMMARY,
  PLAN_TYPE_SERVICE,
  SECTION_ITEM_LABELS,
  SECTION_KEYS_BY_PLAN_TYPE,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_GENERAL_RECOMMENDATIONS,
  SECTION_KEY_GOALS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_OUTCOMES,
  SECTION_KEY_PLOPS,
  SECTION_KEY_PRIORITIES,
  SECTION_KEY_SERVICES,
  SECTION_KEY_SUMMARY,
  SECTION_KEY_SUPPORTS
} from './constants'

import { FILTER_ALL } from './support/FilterSelector'

import sparkleSummary from '../assets/sparkle-summary.svg'

/**
 * Convenience function for dispatching to a plan-type-specific function, with a fallback for when the
 * plan’s type has no associated function. Each candidate function is expected to accept a single
 * argument (the plan).
 *
 * @param {object} options source of parameters
 * @param {object} options.plan the plan to process; its `planType` selects the function to invoke
 * @param {object} options.typeToFunction mapping of plan types to functions that accept the plan
 * @param {*} [options.default] value to return when no function is mapped to the plan’s type
 * @returns the result of the matching plan-type function, or the default value if none matches
 */
const dispatchPlanFunction = ({ plan, typeToFunction, defaultValue }) => {
  const { planType } = plan ?? {}
  const planFunction = typeToFunction[planType]
  return planFunction ? planFunction(plan) : defaultValue
}

/**
 * Convenience method for populating an array of IEP/504 targets (either recommendations or concerns) with
 * their full source objects. For quick lookup, this function takes object maps to goals, services,
 * accommodations, and modifications. The maps object also includes asset details for the original source file(s).
 *
 * @param {object} options source of parameters, as described above
 * @returns the targets array, now with sources assigned
 */
const embedIepSources = ({ targets, maps }) => {
  const { assetDetails, goalsMap, servicesMap, accommodationsMap, modificationsMap, plopsMap } = maps

  targets.forEach(target => {
    const { goals, plops, services, accommodations } = target

    // The IEP data structure actually allows for more than one potential source, so our approach here
    // is to have a precedence…the first truthy source wins!
    const goal = goalsMap[goals[0]]
    const plop = plopsMap[plops[0]]
    const service = servicesMap[services[0]]

    // Note again how accommodations and modifications are modeled in the same array by the back end.
    // `accommodationsMap` and `modificationsMap` are prepared by the model module and the ID should
    // match just one of those maps…
    const accommodation = accommodationsMap[accommodations[0]]
    const modification = modificationsMap[accommodations[0]]

    // Note how we must follow the same precedence for this to correspond.
    const source = goal ?? plop ?? service ?? accommodation ?? modification
    const type = goal
      ? 'goal'
      : plop
        ? 'plop'
        : service
          ? 'service'
          : accommodation
            ? 'accommodation'
            : modification
              ? 'modification'
              : null

    target.source = { ...source, assetDetails, plopsMap, sourceType: type }
    // The data model already sometimes uses `type` ☝🏽
  })

  return targets
}

/**
 * Convenience method for populating an array of IPP targets (either recommendations or concerns) with their
 * full source objects. Unlike the IEP/504 version, this resolves sources by containment: IPP items list the
 * IDs of their recommendations and concerns, so the item that lists a target’s ID is that target’s source.
 * The maps object also includes asset details for the original source file(s).
 *
 * @param {object} options source of parameters, as described above
 * @returns the targets array, now with sources assigned
 */
const embedIppSources = ({ targets, maps }) => {
  const { assetDetails, pogsMap, servicesSupportsMap } = maps

  const targetIdToSource = {}
  const sourceMaps = [pogsMap, servicesSupportsMap]
  sourceMaps.forEach(sourceMap =>
    Object.values(sourceMap ?? {}).forEach(item => {
      const { recommendations = [], concerns = [] } = item ?? {}
      const targetIds = [...recommendations, ...concerns]
      targetIds.forEach(targetId => (targetIdToSource[targetId] = item))
    })
  )

  targets.forEach(target => {
    const source = targetIdToSource[target.id]
    target.source = { ...source, assetDetails, sourceType: source?.type ?? null }
    // A falsy sourceType is treated as a general recommendation downstream ☝🏽
  })

  return targets
}

/**
 * Convenience method for populating an array of targets (either recommendations or concerns) with their
 * full source objects. This is a wrapper for the other two functions that are specific to the plan type.
 * The options object doubles as the dispatch “plan” because it carries the `planType` property.
 *
 * @param {object} options source of parameters, as described above
 * @param {array} options.targets the recommendations or concerns whose sources are to be embedded
 * @param {object} options.maps object maps for resolving each target’s source, plus asset details
 * @param {string} options.planType the type of the plan that the targets belong to
 * @returns the targets array, now with sources assigned, factoring in the plan’s type
 */
const embedSources = options =>
  dispatchPlanFunction({
    plan: options,
    typeToFunction: {
      [PLAN_TYPE_IEP]: embedIepSources,
      [PLAN_TYPE_504]: embedIepSources,
      [PLAN_TYPE_IPP]: embedIppSources
    },
    // In the absence of a (recognized) plan type, the targets are returned as-is, without sources.
    defaultValue: options?.targets
  })

/**
 * Start supporting document extractions, then upload IEP/504. Attach documents to associated goal, if any.
 *
 * @param {string} childId
 * @param {string} planPostId The ID of the IEP/504
 * @param {array} supportingDocumentIds Array of document IDs that will be used in areas of need extractions
 * @param {object} goal The associated goal, if any
 * @param {function} setIepId The `useState` setter hook for the IEP ID, will be called after areas of need
 *                            extractions have begun
 * @param {function} setError The `useState` setter hook for the standard error message. Called with a second
 *                            `{ source: 'attach' }` argument if the document fails to be attached.
 */
const uploadIepDocuments = async ({ childId, planPostId, supportingDocumentIds, goal, setIepId, setError }) => {
  const { id: goalId, relatedDocIds } = goal ?? {}
  if (goalId) {
    try {
      const docsToAdd = [...new Set([planPostId, ...supportingDocumentIds])].filter(id => !relatedDocIds.includes(id))
      if (docsToAdd.length > 0) {
        await patchGoal({ id: goalId, relatedDocIds: [...relatedDocIds, ...docsToAdd] })
      }
    } catch (error) {
      setError(error, { source: 'attach' })
    }
  }

  const startDocExtraction = async id => {
    try {
      await postDocumentExtraction(childId, id)
    } catch (error) {
      setError(error)
    }
  }
  await Promise.all(supportingDocumentIds.map(startDocExtraction))

  if (setIepId) {
    setIepId(planPostId)
  }
}

/**
 * Hide a plan document on behalf of a status tracker’s removal: turn its visibility off, run the caller’s
 * bookkeeping, and report any failure before rethrowing it. The rethrow is the contract `useIepStatus`
 * relies on to learn that a removal failed and clear its “Removing…” scrim, so callers must let it through.
 *
 * @param {string} postId The ID of the plan document to hide
 * @param {function} onHidden Bookkeeping to run once the document is hidden, if any. May be async; a failure
 *                            here is reported and rethrown the same way.
 * @param {function} setError The `useState` setter hook (or error handler) for the standard error message
 */
const removePlanDocument = async ({ postId, onHidden, setError }) => {
  try {
    await patchIepVisibility(postId, { visible: false })

    if (onHidden) {
      await onHidden()
    }
  } catch (error) {
    setError(error)
    throw error
  }
}

/**
 * Quick convenience function for handling the data structure returned by the API.
 *
 * @param {object} statusWrapper object with an IEP status key whose value is the desired IEP stub
 * @returns the wrapped IEP stub
 */
const extractIepFromStatusWrapper = ({ unstarted, pending, completed, error, failed }) =>
  unstarted ?? pending ?? completed ?? error ?? failed

/**
 * Convenience function for taking a concern and returning a version of it that would be considered
 * a “general concern” (i.e., one that is not associated with any goals, services, accommodations, or PLOPS).
 *
 * @param {object} concern the concern to set as a general one.
 * @returns the generalized concern (with goals, services, accommodations, and plops arrays cleared out)
 */
const generalConcern = concern => ({ ...concern, goals: [], services: [], accommodations: [], plops: [] })

/**
 * IPP counterpart of `generalConcern`: takes a concern and returns a version of it that the IPP’s
 * unattached filter (see recommendationPlacement.js) would consider general.
 *
 * @param {object} concern the concern to set as a general one.
 * @returns the generalized concern (with associated items arrays cleared out)
 */
const ippGeneralConcern = concern => ({
  ...concern,
  priorities: [],
  outcomes: [],
  goals: [],
  services: [],
  supports: []
})

/**
 * Takes a concern and returns a version of it that the given plan would consider general,
 * factoring in the plan’s type.
 *
 * @param {object} plan the plan the concern belongs to; its `planType` selects the implementation
 * @param {object} concern the concern to set as a general one
 * @returns the generalized concern, or the concern itself if the plan’s type is unrecognized
 */
const getPlanGeneralConcern = (plan, concern) =>
  dispatchPlanFunction({
    plan,
    typeToFunction: {
      [PLAN_TYPE_IEP]: () => generalConcern(concern),
      [PLAN_TYPE_504]: () => generalConcern(concern),
      [PLAN_TYPE_IPP]: () => ippGeneralConcern(concern)
    },
    defaultValue: concern
  })

/**
 * Convenience function to build API payload for batch review-status-update endpoint.
 *
 * @param {array} idContainers array of objects containing IEP item IDs to update
 * @param {string} reviewStatus the review status to set for all items
 * @returns the payload object for the API call
 */
const getBatchStatusPayload = (idContainers, reviewStatus) =>
  Object.fromEntries(idContainers.map(({ id }) => [id, { reviewStatus }]))

const NO_ITEMS = [] // Fixed empty array to minimize re-renders downstream.

const POG_COLLECTIONS = {
  combinedKey: 'pogs',
  constituentKeys: ['priorities', 'outcomes', 'goals'],
  mapKey: 'pogsMap'
}

const SERVICE_SUPPORT_COLLECTIONS = {
  combinedKey: 'servicesSupports',
  constituentKeys: ['services', 'supports'],
  mapKey: 'servicesSupportsMap'
}

/**
 * Mapping of each IPP item type to the collections on an IPP that hold items of that type: the
 * type’s own subarray, the combined array that the subarray feeds, the subarrays making up that
 * combined array, and the combined array’s lookup map.
 */
const IPP_TYPE_COLLECTIONS = {
  [IPP_TYPE_PRIORITY]: { sublist: 'priorities', ...POG_COLLECTIONS },
  [IPP_TYPE_OUTCOME]: { sublist: 'outcomes', ...POG_COLLECTIONS },
  [PLAN_TYPE_GOAL]: { sublist: 'goals', ...POG_COLLECTIONS },
  [PLAN_TYPE_SERVICE]: { sublist: 'services', ...SERVICE_SUPPORT_COLLECTIONS },
  [IPP_TYPE_SUPPORT]: { sublist: 'supports', ...SERVICE_SUPPORT_COLLECTIONS }
}

// The collections that hold a plan’s recommendation categories, present only on plans that have
// been through getPlanWithRecommendationCategories. Shared by both plan types’ item wiring; the
// IPP mapping below leaves it out because that mapping doubles as the list of item associations.
const RECOMMENDATION_CATEGORY_COLLECTIONS = {
  sublist: 'recommendationCategories',
  mapKey: 'recommendationCategoriesMap'
}

// Likewise for the sections’ summaries, present only on plans that have been through
// getPlanWithSectionSummaries.
const SECTION_SUMMARY_COLLECTIONS = {
  sublist: 'sectionSummaries',
  mapKey: 'sectionSummariesMap'
}

/**
 * Mapping of each IEP/504 item type to the collections on an IEP that hold items of that type. IEPs
 * have no combined arrays, so each type has just its own subarray and that subarray’s lookup map.
 *
 * The entries are ordered by the same source precedence that `embedIepSources` applies, so code that
 * scans this mapping resolves an ID the same way that sources are resolved.
 */
const IEP_TYPE_COLLECTIONS = {
  [PLAN_TYPE_GOAL]: { sublist: 'goals', mapKey: 'goalsMap' },
  [IEP_TYPE_PLOP]: { sublist: 'plops', mapKey: 'plopsMap' },
  [PLAN_TYPE_SERVICE]: { sublist: 'services', mapKey: 'servicesMap' },
  [IEP_TYPE_ACCOMMODATION]: { sublist: 'accommodations', mapKey: 'accommodationsMap' },
  [IEP_TYPE_MODIFICATION]: { sublist: 'modifications', mapKey: 'modificationsMap' },

  // Listed last and never a source, so the scan order above is unaffected.
  [PLAN_TYPE_RECOMMENDATION_CATEGORY]: RECOMMENDATION_CATEGORY_COLLECTIONS,
  [PLAN_TYPE_SECTION_SUMMARY]: SECTION_SUMMARY_COLLECTIONS
}

// The category that general recommendations without one, and general notes not derived from a
// recommendation, fall into. It sorts last, as does an actual category by this name.
const GENERAL_RECOMMENDATION_CATEGORY = 'General'

const isGeneralCategory = category => category === GENERAL_RECOMMENDATION_CATEGORY

/**
 * Orders recommendation categories alphabetically by name, with the general one last.
 *
 * @param {object} left one category item
 * @param {object} right the other
 * @returns the usual comparator result
 */
const recommendationCategoryComparator = ({ category: left }, { category: right }) =>
  isGeneralCategory(left) === isGeneralCategory(right) ? left.localeCompare(right) : isGeneralCategory(left) ? 1 : -1

/**
 * Produces a copy of the given plan with its general recommendations grouped by category into
 * items of their own, one per category, so that the general recommendations page can list them
 * the way the section pages list plan items. The general recommendations are the loose ones no
 * section hosts (see recommendationPlacement.js). A recommendation goes by its own `category`; a
 * general note goes by the category of the recommendation it was derived from, or, like a
 * recommendation without a category, to the general one. A category with a summary among the
 * plan’s `summaries.recommendations` carries it. Idempotent, so it can rerun after the plan
 * changes in memory.
 *
 * @param {object} plan the plan to derive the categories for; its `planType` selects the placement rules
 * @returns a copy of the plan with `recommendationCategories` and `recommendationCategoriesMap`, or
 *          the plan itself if its type is unrecognized
 */
const getPlanWithRecommendationCategories = plan => {
  if (!UNATTACHED_FILTERS[plan?.planType]) {
    return plan
  }

  const { recommendationsMap = {}, summaries } = plan
  const { recommendations: categorySummaries = [] } = summaries ?? {}
  const { generalRecommendations, generalConcerns } = partitionLooseTargets(plan)

  const categories = new Map()

  const categoryFor = name => {
    const category = name || GENERAL_RECOMMENDATION_CATEGORY

    if (!categories.has(category)) {
      categories.set(category, { category, recommendations: [], concerns: [] })
    }

    return categories.get(category)
  }

  generalRecommendations.forEach(recommendation =>
    categoryFor(recommendation.category).recommendations.push(recommendation.id)
  )

  // A note goes by its own category when it was added on a category’s row, else by that of the
  // recommendation it was created from.
  generalConcerns.forEach(concern =>
    categoryFor(concern.category || recommendationsMap[concern.createdFrom]?.category).concerns.push(concern.id)
  )

  // Typed the way model.ipp types an IPP’s items, so that code resolving an item’s collections
  // by its type finds a category’s.
  const recommendationCategories = [...categories.values()]
    .sort(recommendationCategoryComparator)
    .map((category, index) => ({
      ...category,
      id: `category:${category.category}`,
      type: PLAN_TYPE_RECOMMENDATION_CATEGORY,
      number: index + 1,
      summary: categorySummaries.find(summary => summary.category === category.category)?.summary
    }))

  return {
    ...plan,
    recommendationCategories,
    recommendationCategoriesMap: Object.fromEntries(recommendationCategories.map(category => [category.id, category]))
  }
}

/**
 * The ID of the section summary item for a section.
 *
 * @param {string} sectionKey the section’s key
 * @returns the ID its summary item goes by
 */
const getSectionSummaryId = sectionKey => `summary:${sectionKey}`

/**
 * Produces a copy of the given plan with each hosted section’s summary derived as an item of its
 * own, so that a section’s page can lead its list with it: the extractor’s rollup of the section
 * (its parent-facing `summary`, with the rollup’s count, breakdown, and quality kept as `stats`)
 * and the loose recommendations and notes placed in the section (see recommendationPlacement.js).
 * Only a hosted section (one with items, so a page exists to lead) gets an item, and only when its
 * rollup has a summary or its placement holds something, so a plan extracted before rollups
 * existed gains none. Idempotent, so it can rerun after the plan changes in memory.
 *
 * @param {object} plan the plan to derive the summaries for; its `planType` selects the sections
 * @returns a copy of the plan with `sectionSummaries` and `sectionSummariesMap`, or the plan
 *          itself if its type is unrecognized
 */
const getPlanWithSectionSummaries = plan => {
  const sectionKeys = SECTION_KEYS_BY_PLAN_TYPE[plan?.planType]

  if (!sectionKeys) {
    return plan
  }

  const { sections: rollups = {} } = plan.summaries ?? {}
  const { sectionRecommendations, sectionConcerns } = partitionLooseTargets(plan)
  const hosts = getSectionHosts(plan)

  // Typed the way model.ipp types an IPP’s items, so that code resolving an item’s collections
  // by its type finds a summary’s.
  const sectionSummaries = sectionKeys
    .filter(sectionKey => hosts.has(sectionKey))
    .flatMap(sectionKey => {
      const { summary, count, breakdown, quality } = rollups[sectionKey] ?? {}
      const recommendations = sectionRecommendations[sectionKey].map(idMap)
      const concerns = sectionConcerns[sectionKey].map(idMap)

      return summary || recommendations.length > 0 || concerns.length > 0
        ? [
            {
              id: getSectionSummaryId(sectionKey),
              type: PLAN_TYPE_SECTION_SUMMARY,
              sectionKey,
              number: 1,
              summary,
              stats: { count, breakdown, quality },
              recommendations,
              concerns
            }
          ]
        : []
    })

  return {
    ...plan,
    sectionSummaries,
    sectionSummariesMap: Object.fromEntries(sectionSummaries.map(sectionSummary => [sectionSummary.id, sectionSummary]))
  }
}

/**
 * Produces a copy of the given plan with every derived collection its pages lead their lists with:
 * the sections’ summaries and the general recommendations’ categories. The one modeling step every
 * page applies after the plan-type model, so that the derived collections agree wherever the plan
 * goes.
 *
 * @param {object} plan the modeled plan
 * @returns a copy of the plan with its section summaries and recommendation categories
 */
const getPlanWithLeadItems = plan => getPlanWithRecommendationCategories(getPlanWithSectionSummaries(plan))

// An IPP recommendation or concern is attributed to an item by naming it in the array matching that
// item’s type—the same subarrays the items themselves live in.
const IPP_ASSOCIATION_KEYS = Object.values(IPP_TYPE_COLLECTIONS).map(({ sublist }) => sublist)

/**
 * Determines the ID of the IPP item that the given recommendation or concern is attributed to.
 *
 * @param {object} target the recommendation or concern whose attribution is wanted
 * @returns the ID of the item it is attributed to, or undefined if it is attributed to none (which
 *          is what makes it a general recommendation or concern)
 */
const getIppItemIdForTarget = target => {
  const associationKey = IPP_ASSOCIATION_KEYS.find(key => target?.[key]?.length > 0)
  return associationKey ? target[associationKey][0] : undefined
}

// An IEP recommendation or concern is attributed to an item by reference arrays, with the same
// precedence that `embedIepSources` applies. There is no `modifications` array—modifications are
// accommodations to the back end, so `accommodations` covers both.
const IEP_ASSOCIATION_KEYS = ['goals', 'plops', 'services', 'accommodations']

/**
 * Determines the ID of the IEP/504 item that the given recommendation or concern is attributed to.
 *
 * @param {object} target the recommendation or concern whose attribution is wanted
 * @returns the ID of the item it is attributed to, or undefined if it is attributed to none (which
 *          is what makes it a general recommendation or concern)
 */
const getIepItemIdForTarget = target => {
  const associationKey = IEP_ASSOCIATION_KEYS.find(key => target?.[key]?.length > 0)
  return associationKey ? target[associationKey][0] : undefined
}

/**
 * Determines the ID of the plan item that the given recommendation or concern is attributed to,
 * factoring in the plan’s type. The options object doubles as the dispatch “plan” because it
 * carries the `planType` property.
 *
 * @param {object} options source of parameters, as described above
 * @param {string} options.planType the type of the plan that the target belongs to
 * @param {object} options.target the recommendation or concern whose attribution is wanted
 * @returns the ID of the item it is attributed to, or undefined if it is attributed to none
 */
const getPlanItemIdForTarget = options =>
  dispatchPlanFunction({
    plan: options,
    typeToFunction: {
      [PLAN_TYPE_IEP]: ({ target }) => getIepItemIdForTarget(target),
      [PLAN_TYPE_504]: ({ target }) => getIepItemIdForTarget(target),
      [PLAN_TYPE_IPP]: ({ target }) => getIppItemIdForTarget(target)
    },
    defaultValue: undefined
  })

// Every collection an IPP item of a given type lives in: the plan items’ (which also list the
// item associations) plus the derived recommendation categories’, which have no combined array.
const IPP_ITEM_COLLECTIONS = {
  ...IPP_TYPE_COLLECTIONS,
  [PLAN_TYPE_RECOMMENDATION_CATEGORY]: RECOMMENDATION_CATEGORY_COLLECTIONS,
  [PLAN_TYPE_SECTION_SUMMARY]: SECTION_SUMMARY_COLLECTIONS
}

// The collections that hold each plan type’s items, by item type. This is all that distinguishes
// the plan types to the item-change and lookup functions below: an item is found by its ID in a
// type’s subarray, since IEP items already use `type` for other semantics (e.g., a service’s `type`
// is an array) while `model.ipp` stamps the wizard’s type on IPP items, and the ID is what both
// share. The IEP mapping’s order is the source precedence that `embedIepSources` applies.
const PLAN_ITEM_COLLECTIONS = {
  [PLAN_TYPE_IEP]: IEP_TYPE_COLLECTIONS,
  [PLAN_TYPE_504]: IEP_TYPE_COLLECTIONS,
  [PLAN_TYPE_IPP]: IPP_ITEM_COLLECTIONS
}

const NO_ITEM_AND_TYPE = {} // Fixed empty result to minimize re-renders downstream.

/**
 * Locates a plan item by its ID among the plan’s collections: which type’s collections hold it,
 * where in the subarray it sits, and the item itself as the plan holds it.
 *
 * @param {object} plan the plan holding the item
 * @param {string} id the ID of the item to locate
 * @returns an object with the `itemType`, its `collections`, the `items` subarray, and the item’s
 *          `itemIndex` in it; undefined if the ID matches no item (including when the plan’s type
 *          is unrecognized)
 */
const locatePlanItem = (plan, id) => {
  const typeCollections = PLAN_ITEM_COLLECTIONS[plan?.planType] ?? {}

  for (const [itemType, collections] of Object.entries(typeCollections)) {
    const items = plan[collections.sublist] ?? NO_ITEMS
    const itemIndex = items.findIndex(item => item.id === id)

    if (itemIndex >= 0) {
      return { itemType, collections, items, itemIndex }
    }
  }

  return undefined
}

/**
 * Produces a copy of the given plan with the given changes applied to one of its items.
 *
 * The model module has an item’s subarray and its lookup map hold the very same item objects—and,
 * for an IPP, the combined array that the subarray feeds as well—so a change has to be written to
 * all of them or they diverge. Which collections those are comes from the plan type’s mapping; a
 * type without a combined array (every IEP type, and the recommendation categories) has just its
 * subarray and map.
 *
 * An IEP’s `allGoals` and `allAccommodations` are deliberately left stale: nothing in the wizard
 * reads them for review state. Revisit if that changes.
 *
 * @param {object} plan the plan holding the item to change; its `planType` selects the collections
 * @param {object} item the item to change; its ID determines which collections get rewritten
 * @param {object} changes the properties to apply to the item
 * @returns a copy of the plan with the changed item in place, or the given plan itself if the item
 *          is not among the plan’s items (including when the plan’s type is unrecognized)
 */
const getPlanWithItemChanges = (plan, item, changes) => {
  const { id: itemId } = item ?? {}
  const location = locatePlanItem(plan, itemId)

  if (!location) {
    return plan
  }

  const { collections, items, itemIndex } = location
  const { sublist, combinedKey, constituentKeys, mapKey } = collections

  // The changes go onto the item as the plan currently holds it, rather than onto the given item,
  // so that a caller working from a stale copy cannot undo changes made in the meantime.
  const changedItem = { ...items[itemIndex], ...changes }

  const changedPlan = {
    ...plan,
    [sublist]: [...items.slice(0, itemIndex), changedItem, ...items.slice(itemIndex + 1)]
  }

  // The combined array and lookup map derive from the subarrays, so they are refreshed alongside.
  if (combinedKey) {
    changedPlan[combinedKey] = constituentKeys.flatMap(key => changedPlan[key] ?? NO_ITEMS)
  }

  changedPlan[mapKey] = { ...plan[mapKey], [itemId]: changedItem }

  return changedPlan
}

/**
 * Looks up a plan item by its ID, along with the item type that the wizard ascribes to it: the type
 * whose subarray holds the item, scanned in the plan type’s mapping order (for IEP/504 plans, the
 * source precedence that `embedIepSources` applies).
 *
 * @param {object} plan the plan holding the item
 * @param {string} id the ID of the item to look up
 * @returns an object with the `item` and its `itemType`, both undefined if the ID matches no item
 *          (including when the plan’s type is unrecognized)
 */
const getPlanItemById = (plan, id) => {
  const location = locatePlanItem(plan, id)
  return location ? { item: location.items[location.itemIndex], itemType: location.itemType } : NO_ITEM_AND_TYPE
}

/**
 * Produces a copy of the given plan with the given changes applied to one of its recommendations.
 *
 * A recommendation is held both in the plan’s `recommendations` array and in its `recommendationsMap`,
 * and the model module has the two share the very same objects (for every plan type), so a change has
 * to be written to both or they diverge. The review tallies read the array; the recommendation
 * display reads the map.
 *
 * @param {object} plan the plan holding the recommendation to change
 * @param {string} recommendationId the ID of the recommendation to change
 * @param {object} changes the properties to apply to the recommendation
 * @returns a copy of the plan with the changed recommendation in place, or the given plan itself if
 *          the recommendation is not among the plan’s recommendations
 */
const getPlanWithRecommendationChanges = (plan, recommendationId, changes) => {
  const recommendations = plan?.recommendations ?? NO_ITEMS
  const recommendationIndex = recommendations.findIndex(({ id }) => id === recommendationId)

  if (recommendationIndex < 0) {
    return plan
  }

  // The changes go onto the recommendation as the plan currently holds it. That matters more here
  // than it does for items: the status-patching API does not return a recommendation’s association
  // arrays, and an absent association array reads as “general” to the filters below.
  const changedRecommendation = { ...recommendations[recommendationIndex], ...changes }

  return {
    ...plan,

    recommendations: [
      ...recommendations.slice(0, recommendationIndex),
      changedRecommendation,
      ...recommendations.slice(recommendationIndex + 1)
    ],

    recommendationsMap: { ...plan.recommendationsMap, [recommendationId]: changedRecommendation }
  }
}

const reviewedFilter = ({ reviewStatus }) => reviewStatus === REVIEW_STATUS_REVIEWED

/**
 * Convenience function that tallies the review progress of a single section’s items.
 *
 * @param {Array} items the section’s items, each carrying a `reviewStatus`
 * @returns an object with the section’s reviewed count, total count, and reviewed fraction
 */
const sectionFractionReviewed = (items = []) => {
  const total = items.length
  const reviewed = items.filter(reviewedFilter).length
  return { reviewed, total, fraction: total === 0 ? 0 : reviewed / total }
}

// A section’s review obligations are its items plus the loose recommendations placed in it, which
// are reviewed from the same page.
const sectionReviewables = (items = [], sectionRecommendations = NO_ITEMS) => [...items, ...sectionRecommendations]

/**
 * Computes the per-section review progress for an IEP or 504 plan.
 *
 * @param {object} iep the IEP/504 whose per-section review progress is to be computed
 * @returns an object keyed by section (goals, services, accommodations, modifications,
 *          generalRecommendations)
 */
const getIepFractionReviewedBySection = iep => {
  const { goals, services, accommodations, modifications } = iep ?? {}
  const { sectionRecommendations, generalRecommendations } = partitionLooseTargets(iep)

  return {
    goals: sectionFractionReviewed(sectionReviewables(goals, sectionRecommendations[SECTION_KEY_GOALS])),
    services: sectionFractionReviewed(sectionReviewables(services, sectionRecommendations[SECTION_KEY_SERVICES])),

    accommodations: sectionFractionReviewed(
      sectionReviewables(accommodations, sectionRecommendations[SECTION_KEY_ACCOMMODATIONS])
    ),

    modifications: sectionFractionReviewed(
      sectionReviewables(modifications, sectionRecommendations[SECTION_KEY_MODIFICATIONS])
    ),

    generalRecommendations: sectionFractionReviewed(generalRecommendations)
  }
}

/**
 * Computes the per-section review progress for an IPP.
 *
 * Note that the aggregate `pogs` collection deliberately has no entry here: its constituent
 * priorities/goals/outcomes sections are tallied individually, and an aggregate entry would
 * double-count their items when `getFractionReviewed` totals the sections up.
 *
 * @param {object} ipp the IPP whose per-section review progress is to be computed
 * @returns an object keyed by section (priorities, goals, outcomes, services, supports,
 *          generalRecommendations)
 */
const getIppFractionReviewedBySection = ipp => {
  const { priorities, goals, outcomes, services, supports } = ipp ?? {}
  const { sectionRecommendations, generalRecommendations } = partitionLooseTargets(ipp)

  const sectionEntry = (sectionKey, items) =>
    sectionFractionReviewed(sectionReviewables(items, sectionRecommendations[sectionKey]))

  return {
    [SECTION_KEY_PRIORITIES]: sectionEntry(SECTION_KEY_PRIORITIES, priorities),
    [SECTION_KEY_GOALS]: sectionEntry(SECTION_KEY_GOALS, goals),
    [SECTION_KEY_OUTCOMES]: sectionEntry(SECTION_KEY_OUTCOMES, outcomes),
    [SECTION_KEY_SERVICES]: sectionEntry(SECTION_KEY_SERVICES, services),
    [SECTION_KEY_SUPPORTS]: sectionEntry(SECTION_KEY_SUPPORTS, supports),
    [SECTION_KEY_GENERAL_RECOMMENDATIONS]: sectionFractionReviewed(generalRecommendations)
  }
}

/**
 * Function for determining how much of each section of a plan has been reviewed.
 * This is a wrapper for the other two functions that are specific to the plan type.
 *
 * @param {object} plan the plan whose per-section review progress is to be computed
 * @returns an object mapping each of the plan’s sections to its review progress,
 *          factoring in the plan’s type
 */
const getFractionReviewedBySection = plan =>
  dispatchPlanFunction({
    plan,
    typeToFunction: {
      [PLAN_TYPE_IEP]: getIepFractionReviewedBySection,
      [PLAN_TYPE_504]: getIepFractionReviewedBySection,
      [PLAN_TYPE_IPP]: getIppFractionReviewedBySection
    },
    // Unrecognized plans return empty objects, indicating that there are no sections.
    defaultValue: {}
  })

/**
 * Function for determining how much of a plan has been reviewed. This totals up the
 * numerators and denominators of the plan’s per-section review progress.
 *
 * @param {object} plan the plan whose review fraction is to be computed
 * @returns the fraction of the plan that has been reviewed
 */
const getFractionReviewed = plan => {
  const sections = Object.values(getFractionReviewedBySection(plan))

  const { reviewed, total } = sections.reduce(
    (totals, section) => ({
      reviewed: totals.reviewed + section.reviewed,
      total: totals.total + section.total
    }),
    { reviewed: 0, total: 0 }
  )

  return total === 0 ? 0 : reviewed / total
}

/**
 * Convenience function for standardizing the ID to use for indicating a new concern.
 *
 * @param {string} key what distinguishes the spot where the concern is being added from every
 *   other spot that can add one at the same time—typically the ID of the domain object to which the
 *   new concern will be attached
 * @returns the ID to use when identifying what concern is being edited
 */
const getNewConcernIdPlaceholder = key => `${NEW_CONCERN_ID_PLACEHOLDER}-${key}`

/**
 * Function for determining how much of a plan has been reviewed, as an integer percentage.
 * This is getFractionReviewed scaled to 100 and floored.
 *
 * @param {object} plan the plan whose review percentage is to be computed
 * @returns the integer percentage of the plan that has been reviewed
 */
const getPercentReviewed = plan => Math.floor(getFractionReviewed(plan) * 100.0)

/**
 * Utility function for extracting prioritized goals and services from performance areas.
 * It relies on the ID arrays gathered during model mapping to do so: the `performanceAreas`
 * object that it expects is the mapped plan’s `performanceAreas` field (see `model.iep`).
 *
 * @param {array} performanceAreas the array of performance areas with associated goal and service IDs
 * @param {array} priorityPerformanceAreaIds the array of performance area IDs that are marked as priorities
 *
 * @returns object containing two sets: one for prioritized goal IDs and one for prioritized service IDs
 */
const getPrioritizedGoalsAndServices = (performanceAreas, priorityPerformanceAreaIds) =>
  priorityPerformanceAreaIds.reduce(
    (accumulator, current) => {
      const performanceArea = performanceAreas[current]
      if (performanceArea) {
        const { goalIds, serviceIds, accommodationIds, modificationIds } = performanceArea
        const { goalSet, serviceSet, accommodationSet, modificationSet } = accumulator
        return {
          goalSet: goalSet.union(new Set(goalIds)),
          serviceSet: serviceSet.union(new Set(serviceIds)),
          accommodationSet: accommodationSet.union(new Set(accommodationIds)),
          modificationSet: modificationSet.union(new Set(modificationIds))
        }
      } else {
        return accumulator
      }
    },
    { goalSet: new Set(), serviceSet: new Set(), accommodationSet: new Set(), modificationSet: new Set() }
  )

/**
 * Comparator function that will order two goals based on their corresponding goals’ number.
 *
 * @param {object} leftGoal the first goal to compare
 * @param {object} rightGoal the second goal to compare
 * @returns comparator value based on the goals’ `number` attributes
 */
const goalNumberComparator = (leftGoal, rightGoal) => {
  const { number: leftGoalNumber } = leftGoal
  const { number: rightGoalNumber } = rightGoal

  return leftGoalNumber - rightGoalNumber
}

/**
 * Function for creating a comparator function that will order two IDs based on their
 * corresponding objects' indices in the given source array.
 *
 * @param {array} sourceArray array for looking up objects with the given IDs
 * @returns comparator function that will order IDs by their order in the source array
 */
const idIndexComparator = sourceArray => (leftId, rightId) => {
  const leftIndex = sourceArray.findIndex(({ id }) => id === leftId)
  const rightIndex = sourceArray.findIndex(({ id }) => id === rightId)
  return leftIndex - rightIndex
}

/**
 * Comparator for ordering concerns from oldest to newest, so that a newly added concern lands at the
 * end of its list. Concerns without a creation timestamp sort last.
 *
 * @param {object} leftConcern concern to compare
 * @param {object} rightConcern concern to compare against
 * @returns negative, zero, or positive number per standard comparator semantics
 */
const concernCreatedAtComparator = ({ createdAt: leftCreatedAt }, { createdAt: rightCreatedAt }) => {
  if (leftCreatedAt && rightCreatedAt) {
    // We’re taking advantage of the lexical sortability of ISO 8601 timestamps here.
    return leftCreatedAt.localeCompare(rightCreatedAt)
  } else if (leftCreatedAt) {
    return -1
  } else if (rightCreatedAt) {
    return 1
  } else {
    return 0
  }
}

/**
 * Convenience function for taking lists of recommendation and concern IDs then (a) mapping the IDs to their
 * corresponding model objects, and (b) filtering these for the ones that should be visible to the user.
 * An ID with no object behind it in its map is simply not visible, and drops out here.
 *
 * @param {object} parameters defining the recommendation and concern IDs to process
 *
 * @returns an object with the filtered recommendation and concern objects
 */
const getVisibleRecommendationsAndConcerns = ({ recommendationIds, recommendationsMap, concernIds, concernsMap }) => {
  const allRecommendations = recommendationIds
    .map(recommendationId => recommendationsMap[recommendationId])
    .filter(Boolean)

  const allConcerns = concernIds
    .map(concernId => concernsMap[concernId])
    .filter(Boolean)
    .sort(concernCreatedAtComparator)

  const concerns = allConcerns.filter(({ status }) => status === CONCERN_STATUS_OPEN)
  const resolvedConcerns = allConcerns.filter(({ status }) => status === CONCERN_STATUS_RESOLVED)

  const unconvertedRecommendations = allRecommendations.filter(
    ({ id }) => !concerns.find(({ createdFrom }) => createdFrom === id)
  )

  const recommendations = unconvertedRecommendations.filter(
    ({ id, status }) => status !== RECOMMENDATION_STATUS_DISMISSED
  )

  const dismissedRecommendations = unconvertedRecommendations.filter(
    ({ id, status }) => status === RECOMMENDATION_STATUS_DISMISSED
  )

  return { recommendations, concerns, dismissedRecommendations, resolvedConcerns }
}

/**
 * Utility function for determining which items have visible recommendations. “Visibility” is more
 * complicated than it sounds because it factors in a recommendation’s status and whether it has been
 * converted into a concern.
 *
 * @param {array} itemsToFilter list of objects with a recommendations array within
 * @param {object} recommendationsMap map to full recommendation objects
 * @returns subset of itemsToFilter that have visible recommendations
 */
const filterVisibleRecommendations = (itemsToFilter, recommendationsMap, concernsMap) => {
  // First, “harvest” the needed recommendation and concern IDs.
  const recommendationIds = itemsToFilter.flatMap(({ recommendations }) => recommendations)
  const concernIds = itemsToFilter.flatMap(({ concerns }) => concerns)

  // Next, determine the recommendations that “count” for the filter (the visible ones).
  const { recommendations } = getVisibleRecommendationsAndConcerns({
    recommendationIds,
    recommendationsMap,
    concernIds,
    concernsMap
  })

  // We only need their IDs…
  const visibleRecommendationIds = new Set(recommendations.map(idMap))

  // …so we can filter out the items that don’t contain them.
  return itemsToFilter.filter(
    ({ recommendations }) => visibleRecommendationIds.intersection(new Set(recommendations)).size > 0
  )
}

/**
 * Determines the plan-wide visibility in a single pass: the Set of visible recommendation IDs and
 * the Set of open concern IDs. A recommendation’s visibility depends only on its own status and on
 * the plan’s open concerns, so one pass over the whole plan answers the question for every section
 * at once.
 *
 * @param {object} plan the plan whose recommendations and concerns to consider
 * @returns `{ visibleIds, openConcernIds }`
 */
const getPlanVisibility = ({ recommendations = [], concerns = [], recommendationsMap = {}, concernsMap = {} }) => {
  const { recommendations: visibleRecommendations, concerns: openConcerns } = getVisibleRecommendationsAndConcerns({
    recommendationIds: recommendations.map(idMap),
    recommendationsMap,
    concernIds: concerns.map(idMap),
    concernsMap
  })

  return { visibleIds: new Set(visibleRecommendations.map(idMap)), openConcernIds: new Set(openConcerns.map(idMap)) }
}

// Pairs one section’s items with the visible recommendations attached to them plus the visible
// loose ones placed in the section, and the open notes placed there. A recommendation attached to
// two items in the same section is counted once per attachment, as the model’s flattened
// recommendation lists do; a loose recommendation is attached to nothing, so the two sets never
// overlap. An ID with no recommendation behind it is simply not visible.
const sectionVisibleRecommendations = ({
  items = NO_ITEMS,
  sectionRecommendations = NO_ITEMS,
  sectionConcerns = NO_ITEMS,
  visibility,
  recommendationsMap
}) => {
  const { visibleIds, openConcernIds } = visibility

  const attachedRecommendations = items
    .flatMap(({ recommendations = [] }) => recommendations)
    .filter(id => visibleIds.has(id))
    .map(id => recommendationsMap[id])

  const visibleSectionRecommendations = sectionRecommendations.filter(({ id }) => visibleIds.has(id))

  return {
    items,
    recommendations: [...attachedRecommendations, ...visibleSectionRecommendations],
    sectionRecommendations: visibleSectionRecommendations,
    concerns: sectionConcerns.filter(({ id }) => openConcernIds.has(id))
  }
}

// The general section’s “items” are the general recommendations themselves, the same convention
// `getFractionReviewedBySection` follows for its `generalRecommendations` entry.
const generalVisibleRecommendations = (generalRecommendations, generalConcerns, visibility) => {
  const { visibleIds, openConcernIds } = visibility

  return {
    items: generalRecommendations,
    recommendations: generalRecommendations.filter(({ id }) => visibleIds.has(id)),
    concerns: generalConcerns.filter(({ id }) => openConcernIds.has(id))
  }
}

const getIepVisibleRecommendationsBySection = iep => {
  const { goals, services, accommodations, modifications, plops, recommendationsMap = {} } = iep ?? {}
  const { sectionRecommendations, sectionConcerns, generalRecommendations, generalConcerns } =
    partitionLooseTargets(iep)
  const visibility = getPlanVisibility(iep ?? {})

  const sectionEntry = (sectionKey, items) =>
    sectionVisibleRecommendations({
      items,
      sectionRecommendations: sectionRecommendations[sectionKey],
      sectionConcerns: sectionConcerns[sectionKey],
      visibility,
      recommendationsMap
    })

  return {
    [SECTION_KEY_GOALS]: sectionEntry(SECTION_KEY_GOALS, goals),
    [SECTION_KEY_SERVICES]: sectionEntry(SECTION_KEY_SERVICES, services),
    [SECTION_KEY_ACCOMMODATIONS]: sectionEntry(SECTION_KEY_ACCOMMODATIONS, accommodations),
    [SECTION_KEY_MODIFICATIONS]: sectionEntry(SECTION_KEY_MODIFICATIONS, modifications),
    [SECTION_KEY_PLOPS]: sectionEntry(SECTION_KEY_PLOPS, plops),

    [SECTION_KEY_GENERAL_RECOMMENDATIONS]: generalVisibleRecommendations(
      generalRecommendations,
      generalConcerns,
      visibility
    )
  }
}

// Services and supports are reported separately, each with its own page. As with
// `getIppFractionReviewedBySection`, there is no aggregate entry, so totaling the sections never
// double-counts.
const getIppVisibleRecommendationsBySection = ipp => {
  const { priorities, goals, outcomes, services, supports, recommendationsMap = {} } = ipp ?? {}
  const { sectionRecommendations, sectionConcerns, generalRecommendations, generalConcerns } =
    partitionLooseTargets(ipp)
  const visibility = getPlanVisibility(ipp ?? {})

  const sectionEntry = (sectionKey, items) =>
    sectionVisibleRecommendations({
      items,
      sectionRecommendations: sectionRecommendations[sectionKey],
      sectionConcerns: sectionConcerns[sectionKey],
      visibility,
      recommendationsMap
    })

  return {
    [SECTION_KEY_PRIORITIES]: sectionEntry(SECTION_KEY_PRIORITIES, priorities),
    [SECTION_KEY_GOALS]: sectionEntry(SECTION_KEY_GOALS, goals),
    [SECTION_KEY_OUTCOMES]: sectionEntry(SECTION_KEY_OUTCOMES, outcomes),
    [SECTION_KEY_SERVICES]: sectionEntry(SECTION_KEY_SERVICES, services),
    [SECTION_KEY_SUPPORTS]: sectionEntry(SECTION_KEY_SUPPORTS, supports),

    [SECTION_KEY_GENERAL_RECOMMENDATIONS]: generalVisibleRecommendations(
      generalRecommendations,
      generalConcerns,
      visibility
    )
  }
}

/**
 * Partitions a plan’s visible recommendations by section. Every section entry pairs the section’s
 * `items` with the visible `recommendations` that appear in the section—the ones attached to its
 * items plus the loose ones placed in it (also given on their own as `sectionRecommendations`)—and
 * the open `concerns` placed in it; the `generalRecommendations` entry’s items are the general
 * recommendations themselves. The keys are the section keys, so the result can be read alongside
 * `getFractionReviewedBySection`.
 *
 * @param {object} plan the plan to partition
 * @returns an object of `{ items, recommendations, sectionRecommendations, concerns }` entries keyed
 *   by section, or an empty object for an unrecognized plan
 */
const getVisibleRecommendationsBySection = plan =>
  dispatchPlanFunction({
    plan,

    typeToFunction: {
      [PLAN_TYPE_IEP]: getIepVisibleRecommendationsBySection,
      [PLAN_TYPE_504]: getIepVisibleRecommendationsBySection,
      [PLAN_TYPE_IPP]: getIppVisibleRecommendationsBySection
    },

    // Unrecognized plans return empty objects, indicating that there are no sections.
    defaultValue: {}
  })

// The order in which the breakdown names its sections. The IPP follows its assistant’s order, which
// opens with the general recommendations; the IEP names its sections in its assistant’s order but
// keeps the general recommendations for the end of the line.
const IEP_RECOMMENDATION_BREAKDOWN_ORDER = [
  SECTION_KEY_GOALS,
  SECTION_KEY_SERVICES,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_PLOPS,
  SECTION_KEY_GENERAL_RECOMMENDATIONS
]

const RECOMMENDATION_BREAKDOWN_ORDERS = {
  [PLAN_TYPE_IEP]: IEP_RECOMMENDATION_BREAKDOWN_ORDER,
  [PLAN_TYPE_504]: IEP_RECOMMENDATION_BREAKDOWN_ORDER,

  [PLAN_TYPE_IPP]: [
    SECTION_KEY_GENERAL_RECOMMENDATIONS,
    SECTION_KEY_PRIORITIES,
    SECTION_KEY_GOALS,
    SECTION_KEY_OUTCOMES,
    SECTION_KEY_SERVICES,
    SECTION_KEY_SUPPORTS
  ]
}

// Singular and lowercase: the count carries the plurality (“4 priority”).
const RECOMMENDATION_BREAKDOWN_LABELS = {
  ...Object.fromEntries(Object.entries(SECTION_ITEM_LABELS).map(([key, label]) => [key, label.toLowerCase()])),
  [SECTION_KEY_GENERAL_RECOMMENDATIONS]: 'general'
}

/**
 * Summarizes a plan’s visible recommendations as a total and a per-section breakdown such as
 * “4 priority, 9 goal, 10 general”. Sections with no visible recommendations are left out, and
 * both values come from the same segments, so the breakdown always adds up to the total.
 *
 * @param {object} plan the plan to summarize
 * @returns `{ total, label }`, where `label` is the empty string when nothing is visible
 */
const getVisibleRecommendationBreakdown = plan => {
  const bySection = getVisibleRecommendationsBySection(plan)
  const order = RECOMMENDATION_BREAKDOWN_ORDERS[plan?.planType] ?? []

  const segments = order
    .map(key => ({ key, count: bySection[key]?.recommendations.length ?? 0 }))
    .filter(({ count }) => count > 0)

  return {
    total: segments.reduce((total, { count }) => total + count, 0),
    label: segments.map(({ key, count }) => `${count} ${RECOMMENDATION_BREAKDOWN_LABELS[key]}`).join(', ')
  }
}

/**
 * Convenience function for determining how much progress to display in the IEP wizard.
 *
 * @param {object} iep the IEP whose wizard progress is to be computed
 * @returns the 0-100 progress value to show in the IEP wizard
 */
const getWizardProgress = iep =>
  IEP_WIZARD_REVIEW_BASELINE + (100 - IEP_WIZARD_REVIEW_BASELINE) * getFractionReviewed(iep)

/**
 * Function that takes a given IEP and computes the lists to be used for its sections.
 *
 * @param {object} iep the current IEP to use
 * @returns object with the lists to use for the IEP wizard’s sections
 */
const getIepWizardSections = iep => {
  const {
    goals = [],
    accommodations = [],
    modifications = [],
    goalsMap = {},
    performanceAreas = {},
    priorityPerformanceAreaIds = [],
    services = []
  } = iep ?? {}

  const prioritizedGoalsAndServices = getPrioritizedGoalsAndServices(performanceAreas, priorityPerformanceAreaIds)

  // For now, only goals get prioritization.
  const { goalSet } = prioritizedGoalsAndServices

  // We require prioritization only if the goal count meets our threshold.
  const goalsNeedPrioritization = goals.length > GOAL_PRIORITIZATION_THRESHOLD
  const prioritizedGoals = (goalsNeedPrioritization ? [...goalSet] : [])
    .map(id => goalsMap[id])
    .sort(goalNumberComparator)

  return {
    goalsNeedPrioritization,
    prioritizedGoals,
    // TODO The non-prioritized branch sorts the plan’s own goals array in place; sort a copy.
    goals: (goalsNeedPrioritization ? goals.filter(({ id }) => !goalSet.has(id)) : goals).sort(goalNumberComparator),
    services,
    accommodations,
    modifications,
    ...partitionLooseTargets(iep)
  }
}

/**
 * Function that takes a given IPP and computes the lists to be used for its sections.
 *
 * @param {object} ipp the current IPP to use
 * @returns object with the lists to use for the IPP wizard’s sections
 */
const getIppWizardSections = ipp => {
  const {
    pogs = [],
    priorities = [],
    outcomes = [],
    goals = [],
    servicesSupports = [],
    services = [],
    supports = []
  } = ipp ?? {}

  return {
    pogs,
    priorities,
    outcomes,
    goals,
    servicesSupports,
    services,
    supports,
    ...partitionLooseTargets(ipp)
  }
}

/**
 * Function that takes a given IEP and computes the IDs over which the IEP assistant is supposed
 * to iterate.
 *
 * @param {object} iep the current IEP to use
 * @returns the IDs over which the IEP assistant is supposed to iterate
 */
const getIepWizardSequence = iep => {
  const {
    goalsNeedPrioritization,
    prioritizedGoals,
    goals,
    services,
    accommodations,
    modifications,
    sectionRecommendations,
    sectionConcerns,
    generalRecommendations,
    generalConcerns
  } = getIepWizardSections(iep)

  const { recommendationsMap, concernsMap } = iep ?? {}

  const goalsWithRecommendations = filterVisibleRecommendations(goals, recommendationsMap, concernsMap)
  const servicesWithRecommendations = filterVisibleRecommendations(services, recommendationsMap, concernsMap)

  const accommodationsWithRecommendations = filterVisibleRecommendations(
    accommodations,
    recommendationsMap,
    concernsMap
  )

  const modificationsWithRecommendations = filterVisibleRecommendations(modifications, recommendationsMap, concernsMap)

  const goalSequence = goals.map(idMap)
  const goalWithRecommendationsSequence = goalsWithRecommendations.map(idMap)
  const serviceSequence = services.map(idMap).sort(idIndexComparator(services))
  const serviceWithRecommendationsSequence = servicesWithRecommendations.map(idMap)
  const accommodationSequence = accommodations.map(idMap).sort(idIndexComparator(accommodations))

  const accommodationWithRecommendationsSequence = accommodationsWithRecommendations
    .map(idMap)
    .sort(idIndexComparator(accommodationsWithRecommendations))

  const modificationSequence = modifications.map(idMap).sort(idIndexComparator(modifications))

  const modificationWithRecommendationsSequence = modificationsWithRecommendations
    .map(idMap)
    .sort(idIndexComparator(modificationsWithRecommendations))

  // As for the IPP: the raw list is what the general step reviews as a batch, and the visible list
  // is the existence gate for navigation.
  const { recommendations: visibleGeneralRecommendations } = generalVisibleRecommendations(
    generalRecommendations,
    generalConcerns,
    getPlanVisibility(iep ?? {})
  )

  return {
    goalsNeedPrioritization,
    prioritizedGoals: prioritizedGoals.map(idMap),
    goalSequence,
    goalWithRecommendationsSequence,
    serviceSequence,
    serviceWithRecommendationsSequence,
    accommodationSequence,
    accommodationWithRecommendationsSequence,
    modificationSequence,
    modificationWithRecommendationsSequence,
    sectionRecommendations,
    sectionConcerns,
    generalRecommendations,
    visibleGeneralRecommendations,
    generalConcerns
  }
}

/**
 * Function that takes a given IPP and computes the IDs over which the IPP assistant is supposed
 * to iterate.
 *
 * @param {object} ipp the current IPP to use
 * @returns the IDs over which the IPP assistant is supposed to iterate
 */
const getIppWizardSequence = ipp => {
  const {
    pogs,
    priorities,
    outcomes,
    goals,
    servicesSupports,
    services,
    supports,
    sectionRecommendations,
    sectionConcerns,
    generalRecommendations,
    generalConcerns
  } = getIppWizardSections(ipp)

  const pogSequence = pogs.map(idMap)
  const prioritySequence = priorities.map(idMap)
  const outcomeSequence = outcomes.map(idMap)
  const goalSequence = goals.map(idMap)
  const serviceSupportSequence = servicesSupports.map(idMap)
  const serviceSequence = services.map(idMap)
  const supportSequence = supports.map(idMap)

  // The general recommendations come in two views. The raw list is what the general step reviews
  // as a batch on Next: dismissed and note-converted recommendations still have to be marked
  // reviewed. The visible list is the existence gate for navigation, because a recommendation the
  // user can no longer act on should not summon a step to act on it.
  const { recommendations: visibleGeneralRecommendations } = generalVisibleRecommendations(
    generalRecommendations,
    generalConcerns,
    getPlanVisibility(ipp ?? {})
  )

  return {
    pogSequence,
    prioritySequence,
    outcomeSequence,
    goalSequence,
    serviceSupportSequence,
    serviceSequence,
    supportSequence,
    sectionRecommendations,
    sectionConcerns,
    generalRecommendations,
    visibleGeneralRecommendations,
    generalConcerns
  }
}

/**
 * Function that takes a given plan and computes the IDs over which the plan assistant is supposed to iterate.
 * This is a wrapper for the other two functions that are specific to the plan type.
 *
 * @param {object} plan the plan to use
 * @returns the IDs over which the plan assistant is supposed to iterate, factoring in the plan’s type
 */
const getWizardSequence = plan =>
  dispatchPlanFunction({
    plan,
    typeToFunction: {
      [PLAN_TYPE_IEP]: getIepWizardSequence,
      [PLAN_TYPE_504]: getIepWizardSequence,
      [PLAN_TYPE_IPP]: getIppWizardSequence
    },
    // In the absence of a (recognized) plan type, we return a “superset” empty object that has empty
    // versions of all the lists that the other two functions return. This hopes to minimize the need
    // for falsy-checking later in the pipeline.
    defaultValue: {
      goalsNeedPrioritization: false,
      prioritizedGoals: [],
      goalSequence: [],
      goalWithRecommendationsSequence: [],
      serviceSequence: [],
      serviceWithRecommendationsSequence: [],
      accommodationSequence: [],
      accommodationWithRecommendationsSequence: [],
      modificationSequence: [],
      modificationWithRecommendationsSequence: [],
      sectionRecommendations: {},
      sectionConcerns: {},
      generalRecommendations: [],
      visibleGeneralRecommendations: [],
      generalConcerns: [],
      pogSequence: [],
      prioritySequence: [],
      outcomeSequence: [],
      serviceSupportSequence: [],
      supportSequence: []
    }
  })

/**
 * Filter function for detecting an item that has not been reviewed.
 *
 * @param {object} param0 An object that contains a `reviewStatus` attribute.
 * @returns whether the given object’s review status is anything other than "reviewed"
 */
const notReviewed = ({ reviewStatus }) => reviewStatus !== REVIEW_STATUS_REVIEWED

// A wizard continuation names both where the Continue action goes (`path`, relative to the plan
// root) and what to call that destination (`label`). The two live in one descriptor so that the
// decision that picks the path is the same one that picks the label; nothing has to parse a path
// back into a name. Section labels mirror the rundown rows; the ones without a rundown row
// (introduction, IEP priorities, summary) are placeholder copy pending design.
const NO_CONTINUATION = Object.freeze({ path: '', label: '' })

const IEP_CONTINUATIONS = Object.freeze({
  root: { path: '', label: 'Introduction' }, // The “root”-level IEP path (just the IEP ID with no children).
  generalRecommendations: { path: 'general-recommendations', label: 'Our general recommendations' },
  priorities: { path: 'priorities', label: 'Priorities' },
  goals: { path: 'goals', label: 'Goals' }, // Reviewed as a single batch list, so there is no per-item path.
  services: { path: 'services', label: 'Services' },
  accommodations: { path: 'accommodations', label: 'Accommodations' },
  modifications: { path: 'modifications', label: 'Modifications' },
  concernsReview: { path: 'concerns-review', label: 'Final review' },
  summary: { path: 'summary', label: 'Summary' }
})

const IPP_CONTINUATIONS = Object.freeze({
  root: { path: '', label: 'Introduction' }, // The “root”-level IPP path (just the IPP ID with no children).
  generalRecommendations: { path: 'general-recommendations', label: 'Our general recommendations' },
  priorities: { path: 'priorities', label: 'Priorities' }, // Reviewed as a single batch list, so there is no per-item path.
  goals: { path: 'goals', label: 'Goals' }, // Also a single batch list.
  outcomes: { path: 'outcomes', label: 'Outcomes' },
  services: { path: 'services', label: 'Services' },
  supports: { path: 'supports', label: 'Supports' },
  summary: { path: 'summary', label: 'Summary' }
})

/**
 * Function to determine where the IEP wizard should continue based on the current state of the given IEP.
 *
 * @param {object} iep the current IEP to use
 * @returns the continuation descriptor `{ path, label }` for the IEP wizard
 */
const getIepWizardContinuation = iep => {
  const {
    goalsNeedPrioritization,
    prioritizedGoals,
    goals,
    services,
    accommodations,
    modifications,
    sectionRecommendations,
    generalRecommendations,
    generalConcerns
  } = getIepWizardSections(iep)

  // A loose recommendation placed in a section is reviewed from that section’s page, so an
  // unreviewed one sends the user back there even when the section’s items are all reviewed, the
  // same as an unreviewed item does.
  const hasUnreviewedSectionRecommendation = sectionKey => sectionRecommendations[sectionKey].some(notReviewed)

  const hasUnreviewedGoal = goals.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_GOALS)
  const hasUnreviewedService = services.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_SERVICES)

  const hasUnreviewedAccommodation =
    accommodations.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_ACCOMMODATIONS)

  const hasUnreviewedModification =
    modifications.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_MODIFICATIONS)

  // The wizard visits sections in the order goals → services → accommodations → modifications, so this
  // is the first item that it would ask the user to review. Plans without goals—a 504 always, an IEP
  // sometimes—start at whichever section comes first.
  const firstWizardItem = goals[0] ?? services[0] ?? accommodations[0] ?? modifications[0]

  // The assistant opens with the general recommendations, so they gate every section after them.
  // Dismissing a recommendation marks it reviewed as well, so this test covers dismissal without
  // naming it—and it puts the continuation on the same footing as the rundown row and the progress
  // tally, both of which count `reviewStatus`.
  const allGeneralRecommendationsReviewed = generalRecommendations.every(reviewedFilter)
  const anyGeneralRecommendationReviewed = generalRecommendations.some(reviewedFilter)

  const allGeneralRecommendationsDismissed = generalRecommendations.every(
    ({ status }) => status === RECOMMENDATION_STATUS_DISMISSED
  )

  const hasGeneralConcerns = generalConcerns.length > 0

  const generalConcernsReviewed = allGeneralRecommendationsDismissed || hasGeneralConcerns

  // Determining the continuation after the goals is fairly straightforward because all services,
  // accommodations, and modifications are presented in a single step, respectively. We compute this
  // beforehand because it is the fallback case in multiple goal conditions.
  const continuationAfterGoals = hasUnreviewedService
    ? IEP_CONTINUATIONS.services
    : hasUnreviewedAccommodation
      ? IEP_CONTINUATIONS.accommodations
      : hasUnreviewedModification
        ? IEP_CONTINUATIONS.modifications
        : generalConcernsReviewed
          ? IEP_CONTINUATIONS.summary
          : IEP_CONTINUATIONS.concernsReview

  // “Not started” has to account for the general recommendations now that they come first: a user
  // who worked partway through them and left belongs back on that step, not at the introduction.
  return firstWizardItem && notReviewed(firstWizardItem) && !anyGeneralRecommendationReviewed
    ? IEP_CONTINUATIONS.root
    : !allGeneralRecommendationsReviewed
      ? IEP_CONTINUATIONS.generalRecommendations
      : // Goals are reviewed as one list, so the only prioritization stop is the priorities page
        // itself, when the goals need prioritizing and none has been chosen yet.
        goalsNeedPrioritization && prioritizedGoals.length === 0
        ? IEP_CONTINUATIONS.priorities
        : hasUnreviewedGoal
          ? IEP_CONTINUATIONS.goals
          : continuationAfterGoals
}

/**
 * Reports whether a pipeline status has a known progress milestone. Own-property lookup is
 * deliberate—`in` would also match inherited keys like `toString`, whose value is a function.
 *
 * @param {string} status the pipeline status reported by the back end
 * @returns whether the status maps to a milestone percentage
 */
const isKnownPipelineStatus = status => Object.hasOwn(IEP_PROGRESS_MILESTONES, status)

/**
 * Maps a pipeline status to its milestone percentage, advancing a running percent value. Fully
 * completed extraction always reports 100, regardless of the status. Otherwise two invariants hold
 * for every input: an unrecognized status leaves `currentPercent` untouched, and the result never
 * decreases.
 *
 * @param {string} status the pipeline status reported by the back end
 * @param {number} currentPercent the running percent to advance
 * @param {boolean} extractionCompleted whether extraction has fully completed
 * @returns the percent to display for this status
 */
const pipelineStatusToPercent = (status, currentPercent = 0, extractionCompleted = false) =>
  extractionCompleted
    ? 100
    : Math.max(isKnownPipelineStatus(status) ? IEP_PROGRESS_MILESTONES[status] : currentPercent, currentPercent)

/**
 * Function to determine where the IPP assistant should continue based on the current state of the given IPP.
 *
 * @param {object} ipp the current IPP to use
 * @returns the continuation descriptor `{ path, label }` for the IPP assistant
 */
const getIppWizardContinuation = ipp => {
  const { priorities, goals, outcomes, services, supports, sectionRecommendations, generalRecommendations } =
    getIppWizardSections(ipp)

  // As for the IEP: an unreviewed loose recommendation placed in a section sends the user back to
  // that section’s page.
  const hasUnreviewedSectionRecommendation = sectionKey => sectionRecommendations[sectionKey].some(notReviewed)

  const hasUnreviewedPriority =
    priorities.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_PRIORITIES)

  const hasUnreviewedGoal = goals.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_GOALS)
  const hasUnreviewedOutcome = outcomes.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_OUTCOMES)
  const hasUnreviewedService = services.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_SERVICES)
  const hasUnreviewedSupport = supports.some(notReviewed) || hasUnreviewedSectionRecommendation(SECTION_KEY_SUPPORTS)

  // The wizard visits sections in the order priorities → goals → outcomes → services → supports, so
  // this is the first item that it would ask the user to review.
  const firstWizardItem = priorities[0] ?? goals[0] ?? outcomes[0] ?? services[0] ?? supports[0]

  // The assistant opens with the general recommendations, so they gate every section after them.
  // Dismissing a recommendation marks it reviewed as well, so this test covers dismissal without
  // naming it—and it puts the continuation on the same footing as the rundown row and the progress
  // tally, both of which count `reviewStatus`.
  const allGeneralRecommendationsReviewed = generalRecommendations.every(reviewedFilter)
  const anyGeneralRecommendationReviewed = generalRecommendations.some(reviewedFilter)

  // “Not started” has to account for the general recommendations coming first: a user who worked
  // partway through them and left belongs back on that step, not at the introduction.
  return firstWizardItem && notReviewed(firstWizardItem) && !anyGeneralRecommendationReviewed
    ? IPP_CONTINUATIONS.root
    : !allGeneralRecommendationsReviewed
      ? IPP_CONTINUATIONS.generalRecommendations
      : hasUnreviewedPriority
        ? IPP_CONTINUATIONS.priorities
        : hasUnreviewedGoal
          ? IPP_CONTINUATIONS.goals
          : hasUnreviewedOutcome
            ? IPP_CONTINUATIONS.outcomes
            : hasUnreviewedService
              ? IPP_CONTINUATIONS.services
              : hasUnreviewedSupport
                ? IPP_CONTINUATIONS.supports
                : IPP_CONTINUATIONS.summary
}

/**
 * Function to determine where a plan assistant should continue based on the current state of the given plan.
 * This is a wrapper for the other two functions that are specific to the plan type.
 *
 * @param {object} plan the plan to use
 * @returns the continuation descriptor `{ path, label }` for the wizard, factoring in the plan’s type;
 *   `path` is relative to the plan root and `label` is the user-facing name of that destination
 */
const getWizardContinuation = plan =>
  dispatchPlanFunction({
    plan,
    typeToFunction: {
      [PLAN_TYPE_IEP]: getIepWizardContinuation,
      [PLAN_TYPE_504]: getIepWizardContinuation,
      [PLAN_TYPE_IPP]: getIppWizardContinuation
    },
    defaultValue: NO_CONTINUATION
  })

/**
 * Convenience projections of `getWizardContinuation` for callers that need only one half of it.
 */
const getWizardContinuationPath = plan => getWizardContinuation(plan).path
const getWizardContinuationLabel = plan => getWizardContinuation(plan).label

// Various constants utilities for consistent ordering of certain lists.
const SOURCE_TYPE_ORDER = ['goal', 'service', 'accommodation', 'modification', 'plop'] // See `embedSources` above.

const sourceTypeAndNumberComparator = (leftSource, rightSource) => {
  const { sourceType: leftSourceType, number: leftNumber } = leftSource
  const { sourceType: rightSourceType, number: rightNumber } = rightSource

  return leftSourceType === rightSourceType
    ? leftNumber - rightNumber
    : SOURCE_TYPE_ORDER.indexOf(leftSourceType) - SOURCE_TYPE_ORDER.indexOf(rightSourceType)
}

const sourceContainerTypeAndNumberComparator = (leftConcern, rightConcern) => {
  const { source: leftSource } = leftConcern
  const { source: rightSource } = rightConcern
  return sourceTypeAndNumberComparator(leftSource ?? {}, rightSource ?? {})
}

const IPP_SOURCE_TYPE_ORDER = ['priority', 'outcome', 'goal', 'service', 'support'] // See `embedIppSources` above.

const ippSourceTypeAndNumberComparator = (leftSource, rightSource) => {
  const { sourceType: leftSourceType, number: leftNumber } = leftSource
  const { sourceType: rightSourceType, number: rightNumber } = rightSource

  return leftSourceType === rightSourceType
    ? leftNumber - rightNumber
    : IPP_SOURCE_TYPE_ORDER.indexOf(leftSourceType) - IPP_SOURCE_TYPE_ORDER.indexOf(rightSourceType)
}

const ippSourceContainerTypeAndNumberComparator = (leftConcern, rightConcern) => {
  const { source: leftSource } = leftConcern
  const { source: rightSource } = rightConcern
  return ippSourceTypeAndNumberComparator(leftSource ?? {}, rightSource ?? {})
}

const trackSectionReview = (planType, iepId, filter, type) => {
  const trackFunction = filter === FILTER_ALL ? trackIepReviewAll : trackIepReviewRecommendations
  trackFunction(planType, iepId, type)
}

// The sections offered in PrintDialog's "Notes inline with…" checklist, in display order. `hasData`
// decides whether a section is offered at all for a given IEP/504—if it isn’t, it’s left out of the
// checklist. `items` (absent for the summary pseudo-section, which has no per-item notes of its own)
// returns the section’s underlying list, used to test whether any item in it carries an inline
// note—see `sectionHasNotes` below. `label` receives both `planLabel` and
// `planType`—the PLOPs entry needs the latter, since a 504 has no separate "assessments" data of its
// own; it's the same `plops` field as an IEP's, just relabeled "Assessments" (see `plopLabel` in
// ./constants.js for the same 504-specific relabeling elsewhere).
const SUMMARY_SECTION = {
  key: SECTION_KEY_SUMMARY,
  icon: sparkleSummary,
  label: planLabel => `${planLabel} Summary`,
  hasData: plan => Boolean(plan?.fullSummary)
}

const listSection = (key, label, fields = [key]) => ({
  key,
  label,
  items: plan => fields.flatMap(field => plan?.[field] ?? []),
  hasData: plan => fields.some(field => (plan?.[field]?.length ?? 0) > 0)
})

const SECTION_LIST_IEP = [
  SUMMARY_SECTION,
  listSection(SECTION_KEY_GOALS, planLabel => `${planLabel} Goals`),
  listSection(SECTION_KEY_SERVICES, () => 'Services'),
  listSection(SECTION_KEY_ACCOMMODATIONS, () => 'Accommodations'),
  listSection(SECTION_KEY_MODIFICATIONS, () => 'Modifications')
]

const SECTION_LIST_504 = [SUMMARY_SECTION, listSection(SECTION_KEY_ACCOMMODATIONS, () => 'Accommodations')]

const SECTION_LIST_IPP = [
  SUMMARY_SECTION,
  listSection(SECTION_KEY_PRIORITIES, () => 'Priorities'),
  listSection(SECTION_KEY_GOALS, planLabel => `${planLabel} Goals`),
  listSection(SECTION_KEY_OUTCOMES, () => 'Outcomes'),
  listSection(SECTION_KEY_SERVICES, () => 'Services'),
  listSection(SECTION_KEY_SUPPORTS, () => 'Supports')
]

const PLAN_SECTION_LISTS = {
  [PLAN_TYPE_IEP]: SECTION_LIST_IEP,
  [PLAN_TYPE_504]: SECTION_LIST_504,
  [PLAN_TYPE_IPP]: SECTION_LIST_IPP
}

const getSectionList = planType => PLAN_SECTION_LISTS[planType] ?? []

// Whether a section has at least one open concern—used to drive PrintDialog's "Only include
// items with Notes" filter. A section counts as having notes if any of its items references an open
// concern, or if any open concern is categorized under it. The summary pseudo-section is treated
// as "having notes" whenever it has data at all: the report prints the general notes only when the
// summary is selected, so the filter has to leave it selected (and selectable).
const sectionHasNotes = (section, iep) => {
  const { key, hasData, items } = section
  const { concernsMap, concerns } = iep ?? {}

  if (key === SECTION_KEY_SUMMARY) {
    return hasData(iep)
  }

  const { concerns: visibleConcerns } = getVisibleRecommendationsAndConcerns({
    recommendationIds: [],
    recommendationsMap: {},
    concernIds: (concerns ?? []).map(({ id }) => id),
    concernsMap
  })

  const visibleConcernIds = new Set(visibleConcerns.map(({ id }) => id))

  const checkItemHasNotes = item => {
    const { concerns: itemCons = [] } = item ?? {}
    return itemCons.some(id => visibleConcernIds.has(id))
  }

  const hasItemNotes = items(iep).some(checkItemHasNotes)

  const checkConcernMatchesSection = ({ type }) => type?.toLowerCase() === key
  const hasOverallConcern = visibleConcerns.some(checkConcernMatchesSection)

  return hasItemNotes || hasOverallConcern
}

// Builds the `selectedSections` payload PrintDialog sends the /reports and /preview endpoints: a map
// of section key to whether it’s included. Note that the report template currently has no
// independent show/hide flag for PLOPs, so including it here has no visible effect on the document
// until that’s added.
const buildSelectedSectionsPayload = (selectedSections, planType) => {
  const sections = getSectionList(planType)
  const payload = {}
  sections.forEach(({ key }) => {
    payload[key] = selectedSections.has(key)
  })

  if (planType === PLAN_TYPE_IEP) {
    payload[SECTION_KEY_PLOPS] = selectedSections.has(SECTION_KEY_PLOPS)
  }

  return payload
}

/**
 * Determines whether (and how) the given post can be analyzed.
 *
 * @param {object} post the post to check
 * @returns the matching entry of ANALYZABLE_DOCUMENTS, or undefined if the post is not an IEP, 504, or IPP
 */
const getAnalyzableDocument = post => {
  const { category, documentType } = post ?? {}

  return ANALYZABLE_DOCUMENTS.find(
    ({ tags: [categoryIds, documentTypeIds] }) =>
      equalArrayWithOrder(category, categoryIds) && equalArrayWithOrder(documentType, documentTypeIds)
  )
}

/**
 * Reduces a post’s `extractionStatus` to its status name. The API sends the same status wrapper object used by
 * the key documents list (e.g., `{ completed: { id, docDetails, … } }`), whose key is the name. A bare name is
 * accepted as well, since that is what PostDetail puts in place once an extraction has been started from it.
 *
 * @param {string|object} extractionStatus the post’s `extractionStatus`, if any
 * @returns the status name, or undefined if there is no extraction to speak of
 */
const extractionStatusName = extractionStatus => {
  const name = isPlainObject(extractionStatus) ? Object.keys(extractionStatus)[0] : extractionStatus
  return EXTRACTION_STATUSES_NOT_RUNNING.includes(name) ? undefined : name || undefined
}

/**
 * Determines whether an extraction is running or has finished for the given post.
 *
 * @param {object} post the post to check
 * @returns whether the post has an extraction
 */
const hasExtraction = post => Boolean(extractionStatusName(post?.extractionStatus))

/**
 * Determines whether the given post’s extraction has finished.
 *
 * @param {object} post the post to check
 * @returns whether the post’s extraction is complete
 */
const hasCompletedExtraction = post => extractionStatusName(post?.extractionStatus) === EXTRACTION_STATUS_COMPLETED

export {
  GENERAL_RECOMMENDATION_CATEGORY,
  IEP_TYPE_COLLECTIONS,
  IPP_TYPE_COLLECTIONS,
  RECOMMENDATION_CATEGORY_COLLECTIONS,
  SECTION_SUMMARY_COLLECTIONS,
  concernCreatedAtComparator,
  embedSources,
  uploadIepDocuments,
  removePlanDocument,
  extractIepFromStatusWrapper,
  extractionStatusName,
  generalConcern,
  getAnalyzableDocument,
  getBatchStatusPayload,
  getFractionReviewed,
  getFractionReviewedBySection,
  getIepItemIdForTarget,
  getIppItemIdForTarget,
  getNewConcernIdPlaceholder,
  getPercentReviewed,
  getPlanGeneralConcern,
  getPlanItemById,
  getPlanItemIdForTarget,
  getPlanWithItemChanges,
  getPlanWithLeadItems,
  getPlanWithRecommendationCategories,
  getPlanWithSectionSummaries,
  getPlanWithRecommendationChanges,
  getPrioritizedGoalsAndServices,
  getSectionList,
  getSectionSummaryId,
  getVisibleRecommendationBreakdown,
  getVisibleRecommendationsAndConcerns,
  getVisibleRecommendationsBySection,
  getWizardProgress,
  getWizardSequence,
  getWizardContinuation,
  getWizardContinuationLabel,
  getWizardContinuationPath,
  goalNumberComparator,
  hasCompletedExtraction,
  hasExtraction,
  idIndexComparator,
  ippGeneralConcern,
  ippSourceTypeAndNumberComparator,
  ippSourceContainerTypeAndNumberComparator,
  isKnownPipelineStatus,
  recommendationCategoryComparator,
  notReviewed,
  pipelineStatusToPercent,
  sourceTypeAndNumberComparator,
  sourceContainerTypeAndNumberComparator,
  trackSectionReview,
  sectionHasNotes,
  buildSelectedSectionsPayload
}
